import { useTranslation } from 'react-i18next'
import {
  Button,
  Icon,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import dayjs from 'dayjs'
import { DATE_FORMAT_DMY, DATE_FORMAT_DMY_TIME } from '@/constants'
import { ManualHoursReport, ManualHoursHrReport, UserGroups } from '@/types'
import { Indicator } from '@/components/ui'
import { AuthorizationGuard } from '@/components'

export type ManualHoursReportingTableBodyProps = {
  rows: (ManualHoursReport | ManualHoursHrReport)[]
  onOpenDetails: (id: number) => void
  showUserColumn: boolean
  showExportedColumn: boolean
}

export const ManualHoursReportingTableBody = ({
  rows,
  onOpenDetails,
  showUserColumn,
  showExportedColumn,
}: ManualHoursReportingTableBodyProps): JSX.Element => {
  const { t } = useTranslation('manualHoursReporting')

  return (
    <TableBody>
      {rows?.map((row) => {
        const {
          id,
          date,
          amount_minutes,
          created_at,
          updated_at,
          exported_at,
          status,
        } = row

        const user = (row as ManualHoursHrReport).user
        const isPending = status === 'pending'

        return (
          <TableRow key={id}>
            <TableCell>{id}</TableCell>
            {showUserColumn && user && (
              <TableCell>
                {`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
                  user.email}
              </TableCell>
            )}
            <TableCell>
              {date ? dayjs(date).format(DATE_FORMAT_DMY) : '-'}
            </TableCell>
            <TableCell>{amount_minutes}</TableCell>
            <TableCell>
              {created_at
                ? dayjs(created_at).format(DATE_FORMAT_DMY_TIME)
                : '-'}
            </TableCell>
            <TableCell>
              {updated_at
                ? dayjs(updated_at).format(DATE_FORMAT_DMY_TIME)
                : '-'}
            </TableCell>
            {showExportedColumn && (
              <TableCell>
                {exported_at
                  ? dayjs(exported_at).format(DATE_FORMAT_DMY_TIME)
                  : '-'}
              </TableCell>
            )}
            <TableCell>
              <Indicator
                status={status}
                name={t(`status.${status}`)}
                isSmall={false}
              />
            </TableCell>
            <TableCell align="right">
              <Button
                variant="text"
                onClick={() => onOpenDetails(id)}
                endIcon={
                  <Icon
                    sx={{ fontSize: 14, lineHeight: '21px', fontWeight: 600 }}
                  >
                    <ArrowForwardIcon sx={{ height: 14, width: 14 }} />
                  </Icon>
                }
              >
                <Typography variant="body2" fontWeight={600}>
                  <AuthorizationGuard
                    allowedGroups={[UserGroups.HR]}
                    redirect={false}
                    fallback={t('buttons.view')}
                  >
                    {isPending ? t('buttons.validate') : t('buttons.view')}
                  </AuthorizationGuard>
                </Typography>
              </Button>
            </TableCell>
          </TableRow>
        )
      })}
    </TableBody>
  )
}
